/**
 * Projects on this device.
 *
 * The editor currently loses everything on reload — the only thing in
 * `localStorage` is the colour picker's recents. This is the fix, and it is the
 * same work as cloud save: one packed `.rbxcloth` archive per project, exactly
 * the bytes the cloud would get and exactly the bytes "export the file" writes.
 * One format, one serialiser, one code path.
 *
 * **IndexedDB, not `localStorage`.** A layer is a 585×559 canvas; one PNG blows
 * the ~5 MB string quota on its own, and IndexedDB stores bytes natively rather
 * than inflating them through base64.
 *
 * The metadata shape is `LocalProjectMeta` from `shared/migration.ts` — which
 * has taken that type from a store that never existed since the day it was
 * written. `claimedBy` is the load-bearing field: local projects are
 * device-scoped, so on a shared machine we cannot know whose work is sitting
 * there. It stays unowned until somebody explicitly claims it.
 */
import type { GarmentType } from "../../shared/format";
import type { LocalProjectMeta } from "../../shared/migration";
import { peekManifest } from "../../shared/pack";

const DB_NAME = "rcm-projects";
/** v2 split the metadata away from the archives. See `upgrade()`. */
const DB_VERSION = 2;
/** Archives: `{ id, bytes }` and nothing else. */
const STORE = "projects";
/** Metadata: one small record per design, and the only thing a list reads. */
const META = "meta";

/**
 * One archive, keyed by design id.
 *
 * Separate from its metadata on purpose. IndexedDB has no notion of selecting
 * columns — `getAll()` returns whole records — so while the two lived in one
 * store, rendering a list of names pulled every design's bytes into memory to
 * read its title. At the free tier that is a few hundred kilobytes; at creator
 * tier it is five hundred designs of PNG data to answer a question about
 * strings, on a page whose whole job is to be quick.
 */
interface StoredArchive {
  id: string;
  bytes: Uint8Array;
}

/** The old shape: metadata and archive in one record. Read only by `upgrade()`. */
interface LegacyRow extends LocalProjectMeta {
  bytes: Uint8Array;
}

export class LocalStoreUnavailable extends Error {
  constructor(cause: string) {
    super(`local storage unavailable: ${cause}`);
    this.name = "LocalStoreUnavailable";
  }
}

/**
 * Bring the database up to the current shape.
 *
 * **v1 → v2 splits metadata out of the archive rows.** Everything a list needs
 * moves to `meta`, and `projects` keeps only `{ id, bytes }`. The migration
 * runs once, inside the version-change transaction, and is the only place that
 * ever reads both halves together.
 *
 * It also fills in `garment` while it is here, which is the right place for it:
 * the field arrived with pants support, so every earlier row lacks it, and the
 * answer is in the manifest each archive already carries. Doing it here makes
 * it a one-time cost rather than an unzip on every list render — and after this
 * runs, a list has no bytes to read it from anyway.
 *
 * Failures are swallowed per row. One corrupt archive must not abort the
 * version-change transaction and leave the whole database unopenable; that row
 * simply lists without a garment, which is what `garmentOf` already means.
 */
function upgrade(db: IDBDatabase, tx: IDBTransaction | null, oldVersion: number) {
  if (!db.objectStoreNames.contains(STORE)) {
    db.createObjectStore(STORE, { keyPath: "id" });
  }
  if (!db.objectStoreNames.contains(META)) {
    const meta = db.createObjectStore(META, { keyPath: "id" });
    // The list sorts by recency, and the migration planner does too.
    meta.createIndex("updatedAt", "updatedAt");
  }

  // A fresh database has nothing to move.
  if (oldVersion < 1 || !tx) return;

  const archives = tx.objectStore(STORE);
  const meta = tx.objectStore(META);
  const all = archives.getAll();
  all.onsuccess = () => {
    for (const row of all.result as LegacyRow[]) {
      const split = splitLegacyRow(row);
      if (!split) continue;
      meta.put(split.meta);
      // Rewrite the archive row without the metadata riding along.
      archives.put(split.archive);
    }
  };
}

/**
 * One v1 row, separated into what each v2 store keeps.
 *
 * Exported because it is the whole decision the migration makes, and the
 * transaction around it cannot be exercised without an IndexedDB shim. Getting
 * this wrong loses a design's name, its ownership, or its bytes — permanently,
 * on somebody's only copy — so it is the part that has to be tested.
 *
 * Returns null for a row with no archive, which is not a design.
 */
export function splitLegacyRow(
  row: LegacyRow,
): { meta: LocalProjectMeta; archive: StoredArchive } | null {
  const { bytes, ...rest } = row;
  if (!bytes) return null;
  return {
    // `garment` is backfilled from the archive's own manifest, because the
    // field arrived with pants support and every earlier row predates it. After
    // this runs there are no bytes on a list read to work it out from.
    meta: { ...rest, garment: rest.garment ?? garmentOf(bytes) },
    archive: { id: row.id, bytes },
  };
}

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new LocalStoreUnavailable("no indexedDB in this context"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      upgrade(request.result, request.transaction, event.oldVersion);
    };

    request.onsuccess = () => resolve(request.result);
    // Safari in private mode, a full disk, or a user who has blocked storage.
    // Every caller treats this as "no local copy", never as a crash.
    request.onerror = () => reject(new LocalStoreUnavailable(String(request.error)));
    request.onblocked = () => reject(new LocalStoreUnavailable("blocked by another tab"));
  }).catch((err) => {
    // Do not cache a rejected promise: a later call may succeed once the other
    // tab closes, and a permanently poisoned handle would be worse than a retry.
    dbPromise = null;
    throw err;
  });

  return dbPromise;
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const request = run(transaction.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(new LocalStoreUnavailable(String(request.error)));
        transaction.onabort = () => reject(new LocalStoreUnavailable(String(transaction.error)));
      }),
  );
}

/**
 * Both stores, one transaction.
 *
 * A design is its metadata *and* its archive, so writing them in two
 * transactions has a window where a row exists in one store and not the other —
 * a design that lists with no bytes behind it, or bytes no list can reach.
 * IndexedDB gives atomicity across stores for free as long as they are named
 * together, which is the whole reason to ask for both up front.
 */
function bothTx(
  mode: IDBTransactionMode,
  run: (meta: IDBObjectStore, archives: IDBObjectStore) => void,
): Promise<void> {
  return open().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const transaction = db.transaction([META, STORE], mode);
        run(transaction.objectStore(META), transaction.objectStore(STORE));
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(new LocalStoreUnavailable(String(transaction.error)));
        transaction.onabort = () => reject(new LocalStoreUnavailable(String(transaction.error)));
      }),
  );
}

/**
 * Every design on this device, metadata only.
 *
 * Reads the `meta` store, so no archive is touched at all. This used to read
 * whole records — `getAll()` has no way to select columns — which meant a page
 * that shows names and dates loaded every design's PNG data to do it.
 *
 * The garment is already on the row: `upgrade()` backfilled it once, from each
 * archive's own manifest, at the moment the two stores were separated.
 */
export async function listLocalProjects(): Promise<LocalProjectMeta[]> {
  const rows = await tx<LocalProjectMeta[]>(META, "readonly", (store) => store.getAll());
  return rows.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function readLocalProject(id: string): Promise<Uint8Array | null> {
  const row = await tx<StoredArchive | undefined>(STORE, "readonly", (store) => store.get(id));
  return row?.bytes ?? null;
}

/** One design's metadata, without its archive. */
export async function readLocalMeta(id: string): Promise<LocalProjectMeta | null> {
  const row = await tx<LocalProjectMeta | undefined>(META, "readonly", (store) => store.get(id));
  return row ?? null;
}

/**
 * What an archive says it is.
 *
 * No longer on any read path — `upgrade()` does this once per design and the
 * metadata carries the answer afterwards. Kept and exported because it is the
 * rule the migration depends on, and the migration is the one place that cannot
 * be exercised from a test without an IndexedDB shim.
 *
 * Undefined rather than `"shirt"` when the archive will not open: this value is
 * uploaded to an account that has no way to correct it, so a guess is
 * permanent.
 */
export function garmentOf(bytes: Uint8Array): GarmentType | undefined {
  try {
    return peekManifest(bytes).manifest.garmentType;
  } catch {
    return undefined;
  }
}

export interface SaveLocalInput {
  id: string;
  title: string;
  bytes: Uint8Array;
  /** What this design is. Written every save, so it can never go stale. */
  garment?: GarmentType;
  /** A picture of the composite. Omitted keeps whatever is already stored. */
  thumb?: Uint8Array;
  /** Preserved across writes; a save must never silently un-claim a project. */
  claimedBy?: string | null;
  migratedTo?: LocalProjectMeta["migratedTo"];
}

/**
 * Write a design: metadata and archive, atomically.
 *
 * Reads the existing metadata inside the same transaction rather than in a
 * separate one first. Two transactions meant a save could interleave with a
 * `markMigrated` from another tab and write back the ownership it had read
 * before that landed — silently un-claiming a design somebody had just put in
 * their account.
 */
export async function saveLocalProject(input: SaveLocalInput): Promise<LocalProjectMeta> {
  let meta: LocalProjectMeta | null = null;

  await bothTx("readwrite", (metaStore, archives) => {
    const existing = metaStore.get(input.id);
    existing.onsuccess = () => {
      const prev = existing.result as LocalProjectMeta | undefined;
      meta = {
        id: input.id,
        title: input.title,
        updatedAt: Date.now(),
        // Ownership survives an ordinary save. Passing `claimedBy` explicitly is
        // how a claim is made; omitting it must not drop one.
        claimedBy: input.claimedBy !== undefined ? input.claimedBy : (prev?.claimedBy ?? null),
        migratedTo: input.migratedTo ?? prev?.migratedTo,
        sizeBytes: input.bytes.byteLength,
        // Same shape: omitting it keeps what is there rather than erasing it,
        // so a caller that does not know the garment cannot lose it.
        garment: input.garment ?? prev?.garment,
        thumb: input.thumb ?? prev?.thumb,
      };
      metaStore.put(meta);
      archives.put({ id: input.id, bytes: input.bytes } satisfies StoredArchive);
    };
  });

  // `bothTx` resolves on transaction completion, which is strictly after the
  // request above, so this is always set.
  return meta as unknown as LocalProjectMeta;
}

/** Both halves, or neither. A design with orphaned bytes is unreachable. */
export async function deleteLocalProject(id: string): Promise<void> {
  await bothTx("readwrite", (meta, archives) => {
    meta.delete(id);
    archives.delete(id);
  });
}

/**
 * Record that a local project now lives in an account.
 *
 * Separate from `saveLocalProject` because it must be idempotent and must not
 * touch `updatedAt` — a migration is not an edit, and bumping the timestamp
 * would reorder somebody's list for a background operation they did not do.
 */
export async function markMigrated(
  id: string,
  migratedTo: NonNullable<LocalProjectMeta["migratedTo"]>,
): Promise<void> {
  // Metadata only, and read-modify-write inside one transaction: the archive is
  // untouched by a migration, and a separate read would race an autosave.
  await tx<IDBValidKey | undefined>(META, "readwrite", (store) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const row = get.result as LocalProjectMeta | undefined;
      if (row) store.put({ ...row, migratedTo, claimedBy: migratedTo.userId });
    };
    return get as unknown as IDBRequest<IDBValidKey | undefined>;
  });
}

/** Total bytes held locally. Shown so the browser's quota is never a surprise. */
export async function localBytesUsed(): Promise<number> {
  const rows = await listLocalProjects();
  return rows.reduce((n, p) => n + p.sizeBytes, 0);
}

/** Test seam: drop the cached handle so a fresh database can be opened. */
export function resetLocalStore() {
  dbPromise = null;
}
